import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { fetchProducts, imgUrl } from "@/lib/api";
import { lifestyleFor } from "@/data/lifestyle";
import { Motif } from "@/lib/motifs";
import { Butterfly } from "@/components/site/LogoMark";

/* ------------------------------------------------------------------ */
/*  Shared helpers                                                    */
/* ------------------------------------------------------------------ */
const Frame = ({ label, id, aside, children }) => (
  <section id={id} className="hairline-b py-16 md:py-24 scroll-mt-32" data-testid={`preview-${id}`}>
    <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 mb-10 flex flex-wrap items-baseline gap-6">
      <div className="w-8 h-8 rounded-full hairline flex items-center justify-center text-xs">{label.split(" ")[0].replace("Option", "")}</div>
      <h2 className="serif text-3xl md:text-4xl">{label}</h2>
      <p className="text-sm text-ink/60 max-w-[52ch]">{aside}</p>
    </div>
    {children}
  </section>
);

const price = (n) => `₹${n.toLocaleString("en-IN")}`;

/* ------------------------------------------------------------------ */
/*  Option A · Editorial Masthead — 1 hero + 2x2 grid                 */
/* ------------------------------------------------------------------ */
const OptionA = ({ items }) => {
  const [hero, ...rest] = items;
  const grid = rest.slice(0, 4);
  return (
    <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
      <div className="grid md:grid-cols-12 gap-6 md:gap-10">
        {hero && (
          <Link to={`/product/${hero.slug}`} className="md:col-span-7 group block" data-testid={`optA-hero-${hero.slug}`}>
            <div className="overflow-hidden bg-sand">
              <img src={imgUrl(lifestyleFor(hero.slug, (hero.images || [{}])[0].url))} alt={hero.name}
                className="w-full aspect-[5/6] object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.03]" />
            </div>
            <div className="mt-6 flex items-end justify-between">
              <div>
                <div className="nav-item text-ink/60 mb-1">The season's opener</div>
                <div className="serif text-3xl md:text-4xl">{hero.name}</div>
                <div className="text-xs text-ink/60 mt-2">{hero.age_range}</div>
              </div>
              <div className="serif text-2xl">{price(hero.price)}</div>
            </div>
          </Link>
        )}
        <div className="md:col-span-5 grid grid-cols-2 gap-6 md:gap-8">
          {grid.map((p) => (
            <Link key={p.slug} to={`/product/${p.slug}`} className="group block" data-testid={`optA-tile-${p.slug}`}>
              <div className="overflow-hidden bg-sand">
                <img src={imgUrl(lifestyleFor(p.slug, (p.images || [{}])[0].url))} alt={p.name}
                  className="w-full aspect-[4/5] object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.03]" />
              </div>
              <div className="mt-3 flex items-baseline justify-between gap-3">
                <span className="serif text-lg leading-tight">{p.name}</span>
                <span className="text-xs text-ink/70">{price(p.price)}</span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Option B · Uniform Gallery — 3 col, no stagger, hairline dividers */
/* ------------------------------------------------------------------ */
const OptionB = ({ items }) => (
  <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
    <div className="grid grid-cols-2 md:grid-cols-3">
      {items.slice(0, 6).map((p, i) => (
        <Link key={p.slug} to={`/product/${p.slug}`}
          className={`group block p-4 md:p-8 ${i % 3 !== 2 ? "md:border-r md:border-r-stone" : ""} ${i < 3 ? "md:border-b md:border-b-stone" : ""}`}
          style={{ borderColor: "var(--stone)" }}
          data-testid={`optB-tile-${p.slug}`}>
          <div className="overflow-hidden bg-sand">
            <img src={imgUrl(lifestyleFor(p.slug, (p.images || [{}])[0].url))} alt={p.name}
              className="w-full aspect-[4/5] object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.03]" />
          </div>
          <div className="mt-5 flex items-baseline justify-between">
            <span className="serif text-xl md:text-2xl">{p.name}</span>
            <span className="text-sm text-ink/70">{price(p.price)}</span>
          </div>
          <div className="mt-1 flex items-center gap-2">
            <Motif name={p.motif} size={14} strokeColor="var(--accent)" />
            <span className="text-xs text-ink/50">{p.age_range}</span>
          </div>
        </Link>
      ))}
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/*  Option C · Horizontal Filmstrip — drag row of tall cards         */
/* ------------------------------------------------------------------ */
const OptionC = ({ items }) => {
  const stripRef = useRef(null);
  const drag = useRef({ down: false, x: 0, s: 0 });
  const onDown = (e) => { drag.current = { down: true, x: e.pageX, s: stripRef.current.scrollLeft }; };
  const onMove = (e) => { if (drag.current.down) stripRef.current.scrollLeft = drag.current.s - (e.pageX - drag.current.x); };
  const stop = () => { drag.current.down = false; };
  return (
    <div>
      <div ref={stripRef} onMouseDown={onDown} onMouseMove={onMove} onMouseUp={stop} onMouseLeave={stop}
        className="overflow-x-auto no-scrollbar cursor-grab active:cursor-grabbing pl-6 md:pl-12 lg:pl-24 pr-6"
        data-testid="optC-strip">
        <div className="flex gap-6 md:gap-10 pb-4">
          {items.map((p) => (
            <Link key={p.slug} to={`/product/${p.slug}`} className="flex-none w-[280px] md:w-[360px] group" data-testid={`optC-tile-${p.slug}`}>
              <div className="overflow-hidden bg-sand relative">
                <img src={imgUrl(lifestyleFor(p.slug, (p.images || [{}])[0].url))} alt={p.name}
                  className="w-full aspect-[3/4] object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.03]" />
                <div className="absolute bottom-0 left-0 right-0 p-4" style={{ background: "linear-gradient(180deg, rgba(28,26,25,0) 0%, rgba(28,26,25,0.75) 100%)" }}>
                  <div className="serif text-2xl text-ground">{p.name}</div>
                  <div className="flex items-baseline justify-between text-xs text-ground/80 mt-1">
                    <span>{p.age_range}</span><span>{price(p.price)}</span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
          <div className="flex-none w-6" />
        </div>
      </div>
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 mt-4 text-xs text-ink/50">Drag to browse →</div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Option D · Split gallery — sticky image left, indexed list right */
/* ------------------------------------------------------------------ */
const OptionD = ({ items }) => {
  const [active, setActive] = useState(0);
  const cur = items[active] || items[0];
  if (!cur) return null;
  return (
    <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
      <div className="grid md:grid-cols-12 gap-8 md:gap-16">
        <div className="md:col-span-7 md:sticky md:top-32 h-fit">
          <div className="overflow-hidden bg-sand relative">
            <img src={imgUrl(lifestyleFor(cur.slug, (cur.images || [{}])[0].url))} alt={cur.name}
              className="w-full aspect-[5/6] object-cover transition-opacity duration-500" key={cur.slug} data-testid={`optD-image-${cur.slug}`} />
            <div className="absolute top-6 left-6 flex items-center gap-2 bg-ground/85 backdrop-blur px-3 py-1">
              <Motif name={cur.motif} size={16} strokeColor="var(--accent)" />
              <span className="nav-item text-ink/70">Now viewing</span>
            </div>
          </div>
        </div>
        <ul className="md:col-span-5 divide-y" style={{ borderColor: "var(--stone)" }} data-testid="optD-list">
          {items.map((p, i) => (
            <li key={p.slug} onMouseEnter={() => setActive(i)} className="cursor-pointer">
              <Link to={`/product/${p.slug}`} className={`flex items-baseline justify-between py-5 transition-colors ${active === i ? "text-ink" : "text-ink/50 hover:text-ink"}`} data-testid={`optD-item-${p.slug}`}>
                <div>
                  <div className="text-xs tracking-widest uppercase">
                    <span className="text-ink/40 mr-3">{String(i + 1).padStart(2, "0")}</span>{p.motif}
                  </div>
                  <div className="serif text-3xl md:text-4xl mt-1 leading-tight">{p.name}</div>
                  <div className="text-xs text-ink/50 mt-1">{p.age_range}</div>
                </div>
                <div className="serif text-xl">{price(p.price)}</div>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Option E · The Compass — rotating almanac ring                    */
/* ------------------------------------------------------------------ */
const OptionE = ({ items }) => {
  const six = items.slice(0, 6);
  const [active, setActive] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setActive((a) => (a + 1) % six.length), 3500);
    return () => clearInterval(t);
  }, [six.length]);
  const cur = six[active];
  if (!cur) return null;
  const radius = 260;
  return (
    <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
      <div className="relative mx-auto" style={{ width: "100%", maxWidth: 720, height: 640 }} data-testid="optE-compass">
        {/* concentric hairline rings */}
        <div className="absolute inset-0 flex items-center justify-center">
          {[520, 400, 280].map((d, i) => (
            <div key={i} className="absolute rounded-full" style={{ width: d, height: d, border: "1px solid var(--stone)", opacity: 0.5 - i * 0.12 }} />
          ))}
        </div>
        {/* orbiting piece dots */}
        {six.map((p, i) => {
          const angle = (i / six.length) * Math.PI * 2 - Math.PI / 2;
          const x = Math.cos(angle) * radius;
          const y = Math.sin(angle) * radius;
          const on = i === active;
          return (
            <button
              key={p.slug}
              onMouseEnter={() => setActive(i)}
              className="absolute top-1/2 left-1/2 flex flex-col items-center gap-2 transition-transform duration-500"
              style={{ transform: `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))` }}
              data-testid={`optE-dot-${p.slug}`}
            >
              <div className={`w-16 h-16 md:w-20 md:h-20 rounded-full overflow-hidden transition-all duration-500 ${on ? "ring-2 ring-[color:var(--accent)] scale-110" : "opacity-70 hover:opacity-100"}`}
                style={{ border: "1px solid var(--stone)" }}>
                <img src={imgUrl(lifestyleFor(p.slug, (p.images || [{}])[0].url))} alt={p.name} className="w-full h-full object-cover" />
              </div>
              <span className={`text-[10px] tracking-[0.2em] uppercase transition-opacity duration-500 ${on ? "text-accent opacity-100" : "text-ink/50 opacity-70"}`}>{p.motif}</span>
            </button>
          );
        })}
        {/* center feature */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center max-w-[240px]">
          <Link to={`/product/${cur.slug}`} className="block group" data-testid={`optE-center-${cur.slug}`}>
            <div className="w-40 h-52 md:w-48 md:h-60 mx-auto overflow-hidden mb-4" style={{ border: "1px solid var(--stone)" }}>
              <img key={cur.slug} src={imgUrl(lifestyleFor(cur.slug, (cur.images || [{}])[0].url))} alt={cur.name}
                className="w-full h-full object-cover transition-opacity duration-700" />
            </div>
            <div className="flex items-center justify-center gap-2 mb-1">
              <Motif name={cur.motif} size={14} strokeColor="var(--accent)" />
              <span className="nav-item text-ink/50">{cur.age_range}</span>
            </div>
            <div className="serif text-2xl leading-tight">{cur.name}</div>
            <div className="text-sm text-ink/70 mt-1">{price(cur.price)}</div>
          </Link>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Option F · The Herbarium Album — taped-in specimens with tags     */
/* ------------------------------------------------------------------ */
const OptionF = ({ items }) => {
  const tilts = [-3.5, 2.2, -1.4, 3.1, -2.6, 1.8];
  const tapeStyles = [
    { top: -6, left: 16, rotate: -18 },
    { top: -8, right: 22, rotate: 14 },
  ];
  return (
    <div className="relative">
      <div className="absolute inset-0" style={{
        background: "repeating-linear-gradient(90deg, rgba(200,180,150,0.06) 0px, rgba(200,180,150,0.06) 1px, transparent 1px, transparent 40px), repeating-linear-gradient(0deg, rgba(200,180,150,0.06) 0px, rgba(200,180,150,0.06) 1px, transparent 1px, transparent 40px), radial-gradient(ellipse at center, rgba(232,223,209,0.5) 0%, rgba(246,241,233,0) 70%)"
      }} />
      <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 md:gap-x-24 gap-y-16 md:gap-y-24">
          {items.slice(0, 6).map((p, i) => {
            const t = tilts[i % tilts.length];
            const leftSide = i % 2 === 0;
            return (
              <Link key={p.slug} to={`/product/${p.slug}`} className="group grid grid-cols-5 gap-4 items-center" data-testid={`optF-item-${p.slug}`}>
                <div className={`col-span-3 relative ${leftSide ? "" : "md:order-2 md:col-start-3"}`} style={{ transform: `rotate(${t}deg)`, transformOrigin: "center", transition: "transform 500ms" }}>
                  <div className="relative">
                    <div className="p-2 bg-ground shadow-[0_6px_20px_-8px_rgba(28,26,25,0.35)]">
                      <img src={imgUrl(lifestyleFor(p.slug, (p.images || [{}])[0].url))} alt={p.name} className="w-full aspect-[4/5] object-cover" style={{ filter: "sepia(0.05) saturate(0.95)" }} />
                    </div>
                    {tapeStyles.map((ts, ti) => (
                      <div key={ti} className="absolute" style={{
                        top: ts.top, left: ts.left, right: ts.right,
                        width: 60, height: 18,
                        background: "rgba(232,223,209,0.75)",
                        transform: `rotate(${ts.rotate}deg)`,
                        boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                      }} />
                    ))}
                  </div>
                </div>
                <div className={`col-span-2 ${leftSide ? "" : "md:order-1"}`}>
                  <div className="relative" style={{ transform: `rotate(${-t / 2}deg)` }}>
                    <div className="serif text-3xl md:text-4xl leading-tight">{p.name}</div>
                    <div className="flex items-center gap-3 mt-3 mb-4">
                      <Motif name={p.motif} size={22} strokeColor="var(--accent)" />
                      <span className="text-[10px] tracking-[0.2em] uppercase text-ink/60">{p.motif} · specimen 0{i + 1}</span>
                    </div>
                    <div className="hairline-t hairline-b py-3 my-3">
                      <div className="flex justify-between text-xs text-ink/70">
                        <span>Age</span><span>{p.age_range}</span>
                      </div>
                      <div className="flex justify-between text-xs text-ink/70 mt-1">
                        <span>Collected</span><span>Winter · Bengaluru</span>
                      </div>
                      <div className="flex justify-between text-sm mt-2">
                        <span className="nav-item">Price</span><span className="serif text-lg">{price(p.price)}</span>
                      </div>
                    </div>
                    <span className="crimson-link nav-item !text-[11px]">Open the entry →</span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Option G · Cinema of the Day — tint shifts as you scroll strip    */
/* ------------------------------------------------------------------ */
const TIMES = [
  { name: "Dawn",       hue: "linear-gradient(180deg, #E8D9C7 0%, #F3D9C9 60%, #F6F1E9 100%)" },
  { name: "Morning",    hue: "linear-gradient(180deg, #F3E8D0 0%, #F6F1E9 100%)" },
  { name: "Noon",       hue: "linear-gradient(180deg, #F6F1E9 0%, #EFE8D9 100%)" },
  { name: "Golden hour",hue: "linear-gradient(180deg, #F4CFA4 0%, #E9B287 60%, #F3D9C9 100%)" },
  { name: "Dusk",       hue: "linear-gradient(180deg, #C9B6CE 0%, #D9A9AC 60%, #F3D9C9 100%)" },
  { name: "Blue hour",  hue: "linear-gradient(180deg, #6E7E9B 0%, #9BAAC2 60%, #C9B6CE 100%)" },
];
const OptionG = ({ items }) => {
  const stripRef = useRef(null);
  const drag = useRef({ down: false, x: 0, s: 0 });
  const onDown = (e) => { drag.current = { down: true, x: e.pageX, s: stripRef.current.scrollLeft }; };
  const onMove = (e) => { if (drag.current.down) stripRef.current.scrollLeft = drag.current.s - (e.pageX - drag.current.x); };
  const stop = () => { drag.current.down = false; };
  return (
    <div ref={stripRef} onMouseDown={onDown} onMouseMove={onMove} onMouseUp={stop} onMouseLeave={stop}
      className="overflow-x-auto no-scrollbar cursor-grab active:cursor-grabbing" data-testid="optG-strip">
      <div className="flex">
        {items.slice(0, 6).map((p, i) => {
          const t = TIMES[i % TIMES.length];
          const dark = i >= 4; // dusk/blue hour = darker text on light copy
          return (
            <div key={p.slug} className="flex-none w-[85vw] md:w-[62vw] lg:w-[46vw] px-4 md:px-10 py-16 md:py-24 relative" style={{ background: t.hue }} data-testid={`optG-scene-${p.slug}`}>
              <div className="relative z-10">
                <div className={`text-[10px] tracking-[0.3em] uppercase mb-3 ${dark ? "text-ground/80" : "text-ink/70"}`}>{t.name}</div>
                <h3 className={`serif text-4xl md:text-5xl leading-tight ${dark ? "text-ground" : "text-ink"}`}>{p.name}</h3>
                <p className={`mt-3 text-sm max-w-[36ch] ${dark ? "text-ground/80" : "text-ink/70"}`}>{p.hook}</p>
                <div className={`mt-4 flex items-center gap-4 text-sm ${dark ? "text-ground/80" : "text-ink/70"}`}>
                  <span className="serif text-xl">{price(p.price)}</span>
                  <span>·</span>
                  <span>{p.age_range}</span>
                </div>
                <Link to={`/product/${p.slug}`} className={`inline-block mt-6 text-[11px] tracking-[0.22em] uppercase border-b pb-[2px] ${dark ? "text-ground border-ground" : "text-ink border-ink"}`} data-testid={`optG-see-${p.slug}`}>See the piece →</Link>
              </div>
              <div className="mt-8 relative">
                <img src={imgUrl(lifestyleFor(p.slug, (p.images || [{}])[0].url))} alt={p.name} className="w-full aspect-[4/5] object-cover" style={{ filter: dark ? "brightness(0.95)" : "none" }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Page                                                              */
/* ------------------------------------------------------------------ */
const CollectionPreview = () => {
  const [items, setItems] = useState([]);
  useEffect(() => { fetchProducts({ category: "girls" }).then((all) => setItems(all.slice(0, 6))); }, []);
  const ready = items.length >= 4;

  return (
    <div className="pt-32 md:pt-40 pb-24" data-testid="collection-preview-page">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 mb-16">
        <div className="flex items-center gap-3 mb-4"><Butterfly size={22} /><span className="nav-item text-ink/60">Preview</span></div>
        <h1 className="serif text-5xl md:text-6xl leading-[1.02]">
          Four ways to hang <em className="italic">First of the season</em>.
        </h1>
        <p className="mt-6 text-ink/70 max-w-[64ch] leading-relaxed">
          Same six girls&rsquo; pieces, four different layouts. Tell me the number that lands — I&rsquo;ll wire it into the home page.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <a href="#option-a" className="nav-item hairline px-3 py-2 hover:bg-ink hover:text-ground">A · Editorial Masthead</a>
          <a href="#option-b" className="nav-item hairline px-3 py-2 hover:bg-ink hover:text-ground">B · Uniform Gallery</a>
          <a href="#option-c" className="nav-item hairline px-3 py-2 hover:bg-ink hover:text-ground">C · Filmstrip</a>
          <a href="#option-d" className="nav-item hairline px-3 py-2 hover:bg-ink hover:text-ground">D · Split Index</a>
          <a href="#option-e" className="nav-item hairline px-3 py-2 hover:bg-accent hover:text-ground border-accent text-accent">E · The Compass ✦</a>
          <a href="#option-f" className="nav-item hairline px-3 py-2 hover:bg-accent hover:text-ground border-accent text-accent">F · Herbarium Album ✦</a>
          <a href="#option-g" className="nav-item hairline px-3 py-2 hover:bg-accent hover:text-ground border-accent text-accent">G · Cinema of the Day ✦</a>
        </div>
      </div>

      {!ready ? (
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 text-sm text-ink/60">Loading pieces…</div>
      ) : (
        <>
          <Frame id="option-a" label="Option A · Editorial Masthead" aside="One hero piece on the left commands the section, four supporting tiles in a tidy 2×2 on the right. Magazine cover energy.">
            <OptionA items={items} />
          </Frame>
          <Frame id="option-b" label="Option B · Uniform Gallery" aside="Same aspect on every tile, no stagger, hairline dividers between rows. Quiet, catalogue-like, easy on the eye.">
            <OptionB items={items} />
          </Frame>
          <Frame id="option-c" label="Option C · Filmstrip" aside="A single horizontal drag row of tall cards with name and price laid over the image. Feels like walking a rack.">
            <OptionC items={items} />
          </Frame>
          <Frame id="option-d" label="Option D · Split Index" aside="Sticky lifestyle photo on the left, a numbered text index on the right. Hover a name to swap the image. Editorial gallery feel.">
            <OptionD items={items} />
          </Frame>
          <Frame id="option-e" label="Option E · The Compass ✦" aside="A slow rotating almanac. Six pieces orbit around a central feature that changes every 3.5 seconds. Hover a piece to lock it in. Fragrance-wheel energy.">
            <OptionE items={items} />
          </Frame>
          <Frame id="option-f" label="Option F · Herbarium Album ✦" aside="Each piece taped into a designer's album like a botanical specimen — slight rotation, warm tape corners, a hand-lettered card with age, collection date and price. Tactile, slow, one-of-a-kind.">
            <OptionF items={items} />
          </Frame>
          <Frame id="option-g" label="Option G · Cinema of the Day ✦" aside="A horizontal filmstrip where each scene sits under a different light — dawn, morning, noon, golden hour, dusk, blue hour. The ambient tint changes as you drag through the day. Cinematic.">
            <OptionG items={items} />
          </Frame>
        </>
      )}
    </div>
  );
};

export default CollectionPreview;
